import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import UseContext from '../../../../../../hooks/useContext';
import ApiDatabase from '../../../../../../server';
import Banner from '../../../../../banner/banner';
import Divider from '../../../../../divider/divider';
import ModalBody from '../../../../components/body';
import UseModal from '../../../../useModal';

import {
  DocumentViewer,
  DocumentSidebar,
  ModalHeader,
  UserInfoGrid,
  ValidationActions,
  DateInput,
  useAdminModalFiles,
  useAdminModalTempWorker,
  useAllUserDocuments,
  parseDateFromAPI,
  openNextUnverifiedDocument,
} from '../shared';

const AdminModalFileAcceptVisa = () => {
  const { toggle } = UseModal();
  const {
    adminType,
    adminTempWorkerId,
    adminUserId,
    adminFileName,
    setUpdateTempWorker,
    setOpenModal,
    setAdminType,
  } = UseContext();
  
  const [numberVisa, setNumberVisa] = useState('');
  const [dateObtained, setDateObtained] = useState(null);
  const [dateExpiration, setDateExpiration] = useState(null);
  
  const token = localStorage.getItem('xsrfToken');

  // Hooks centralisés
  const { docs, isWaiting } = useAdminModalFiles({
    token,
    adminUserId,
    adminType,
    adminFileName,
    enabled: !!adminUserId && !!adminType,
  });

  const { tempWorker, user } = useAdminModalTempWorker({
    token,
    adminTempWorkerId,
    enabled: !!adminTempWorkerId,
  });

  // Récupérer tous les documents de l'utilisateur pour la sidebar
  const { allDocuments, isLoading: isLoadingAllDocs } = useAllUserDocuments({
    token,
    adminUserId,
    currentType: adminType,
    enabled: !!adminUserId && !isWaiting,
  });

  // Charger les données existantes du tempWorker
  useEffect(() => {
    if (tempWorker._id) {
      setNumberVisa(tempWorker.visa?.number_visa ?? '');
      setDateObtained(
        tempWorker.visa?.start_validity
          ? parseDateFromAPI(tempWorker.visa.start_validity)
          : null
      );
      setDateExpiration(
        tempWorker.visa?.end_validity
          ? parseDateFromAPI(tempWorker.visa.end_validity)
          : null
      );
    }
  }, [tempWorker]);

  const modalAcceptSubmit = () => {
    if (
      !token ||
      !adminTempWorkerId ||
      !adminType ||
      !numberVisa ||
      dateObtained === false ||
      dateExpiration === false
    ) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    if (dateObtained && dateExpiration) {
      const obtainedDate = new Date(dateObtained);
      const expirationDate = new Date(dateExpiration);
      
      obtainedDate.setHours(0, 0, 0, 0);
      expirationDate.setHours(0, 0, 0, 0);
      
      if (expirationDate <= obtainedDate) {
        toast.error('La date de fin de séjour doit être supérieure à la date d\'obtention');
        return;
      }
    }

    ApiDatabase.postAcceptTempWorkerFileVisaAdmin(
      {
        token,
        adminTempWorkerId,
        adminType,
        numberVisa,
        dateObtained,
        dateExpiration,
      },
      (data) => {
        setUpdateTempWorker({ tempWorkerId: data._id, documentType: adminType });
        toast.success('Le Visa a été accepté');
        
        // Ouvrir le prochain document non vérifié
        openNextUnverifiedDocument({
          tempWorker,
          allDocuments,
          currentType: adminType,
          setAdminType,
          setOpenModal,
          toggle,
        });
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
      }
    );
  };

  const handleRefuse = () => {
    toggle();
    setOpenModal('ad_file_refuse');
  };

  const handleOpenDocumentModal = (type, modalName) => {
    setAdminType(type);
    toggle();
    setOpenModal(modalName);
  };

  return (
    <ModalBody>
      <div className="flex gap-4 h-[750px]">
        {/* Section 1: Autres documents */}
        <DocumentSidebar
          allDocuments={allDocuments}
          isLoading={isLoadingAllDocs}
          currentType={adminType}
          onOpenDocumentModal={handleOpenDocumentModal}
          tempWorker={tempWorker}
        />
        
        {/* Section 2: Document Viewer */}
        <div className="flex-1 min-w-0 bg-gray-50 rounded-lg overflow-hidden">
          <DocumentViewer docs={docs} isWaiting={isWaiting} />
        </div>
        
        {/* Section 3: User Info + Inputs + Boutons */}
        <div className="w-80 flex flex-col gap-3 bg-gray-50 rounded-lg p-4 overflow-y-auto">
          <ModalHeader
            adminType={adminType}
            tempWorker={tempWorker}
            docsCount={docs.length}
            onClose={toggle}
          />
          
          <UserInfoGrid
            user={user}
            validationStatus={tempWorker.visa?.validated}
            reason={tempWorker.visa?.reason}
            showNationality={true}
            nationalityText={tempWorker.visa?.type ? 'Hors UE' : 'UE, EE, Suisse'}
            additionalBanner={
              tempWorker.visa?.validated === '1' && (
                <div className="col-span-2">
                  <Banner type="warning">
                    {tempWorker.identity?.type === 'Passeport'
                      ? 'Un passeport'
                      : "Une carte d'identité"}{' '}
                    devra aussi être vérifié pour valider ce candidat.
                  </Banner>
                </div>
              )
            }
          />
          
          <Divider />
          
          <div className="w-full">
            <label className="ft-sm text-gray-500">
              <div>N° du Visa</div>
              <input
                type="text"
                className="w-full h-10 pl-3 pr-3 ft-b border rounded-lg text-gray-900 uppercase"
                value={numberVisa}
                onChange={(e) => setNumberVisa(e.target.value.toUpperCase())}
              />
            </label>
          </div>
          
          <DateInput
            label="Date d'obtention (optionnel)"
            value={dateObtained}
            onChange={setDateObtained}
          />
          
          <DateInput
            label="Date de fin de séjour (optionnel)"
            value={dateExpiration}
            onChange={setDateExpiration}
          />
          
          <ValidationActions onRefuse={handleRefuse} onAccept={modalAcceptSubmit} />
        </div>
      </div>
    </ModalBody>
  );
};

export default AdminModalFileAcceptVisa;
